// frontend/src/components/responsive-fixes/HeaderMobilePanel.tsx
//
// Phone-friendly user-menu (audit M7). On <600px the desktop user-menu
// dropdown becomes a full-width slide-down panel anchored under the
// header. On phablet/tablet/desktop, the existing dropdown stays —
// Header.tsx gates this component on responsive.isPhone.
//
// Same content as the desktop dropdown (user info, role pill, profile/
// admin/dashboard/help links, sign-out) but laid out for thumb reach
// with 44+ tap targets.

import React, { useEffect } from "react";
import { BRAND } from "../../branding";

export interface HeaderMobileMenuItem {
  href: string;
  icon: string;
  label: string;
  /** When set, only users in these roles see this item. */
  roles?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  user: { name: string; role: string; phone: string };
  roleInfo: { label: string; icon: string; color: string };
  items: HeaderMobileMenuItem[];
  onLogout: () => void;
  /** Optional offset from top of viewport (typically the header height). */
  topOffset?: number;
}

const HeaderMobilePanel: React.FC<Props> = ({
  open,
  onClose,
  user,
  roleInfo,
  items,
  onLogout,
  topOffset = 64,
}) => {
  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.22s ease",
          zIndex: 990,
        }}
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="User menu"
        aria-hidden={!open}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: topOffset,
          background: "rgba(9,19,39,0.98)",
          backdropFilter: "blur(60px)",
          WebkitBackdropFilter: "blur(60px)",
          borderBottom: "0.5px solid rgba(189,178,255,0.18)",
          boxShadow: "0 16px 50px rgba(0,0,0,0.55)",
          transform: open ? "translateY(0)" : "translateY(-110%)",
          transition: "transform 0.28s cubic-bezier(0.32,0.72,0.32,1)",
          zIndex: 991,
          fontFamily: BRAND.fontFamily,
          color: "#dde5ff",
          maxHeight: `calc(100dvh - ${topOffset}px)`,
          overflowY: "auto",
        }}
      >
        {/* Identity */}
        <div
          style={{
            padding: "16px 18px 14px",
            borderBottom: "0.5px solid rgba(189,178,255,0.08)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
            {user.name}
          </div>
          <div
            style={{
              color: "rgba(221,229,255,0.4)",
              fontSize: 12,
              marginBottom: 10,
            }}
          >
            +{user.phone}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(189,178,255,0.08)",
              borderRadius: 8,
              padding: "6px 10px",
            }}
          >
            <span style={{ fontSize: 14 }}>{roleInfo.icon}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: roleInfo.color,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {roleInfo.label}
            </span>
          </div>
        </div>

        {/* Items */}
        <nav style={{ padding: "8px 10px" }}>
          {items
            .filter((it) => !it.roles || it.roles.includes(user.role))
            .map((it) => (
              <a
                key={it.href}
                href={it.href}
                onClick={onClose}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  minHeight: 44,
                  borderRadius: 10,
                  color: "#dde5ff",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  transition: "background 0.15s",
                  touchAction: "manipulation",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 20, color: "#bdb2ff" }}
                >
                  {it.icon}
                </span>
                {it.label}
              </a>
            ))}

          <div
            style={{
              borderTop: "0.5px solid rgba(189,178,255,0.08)",
              marginTop: 8,
              paddingTop: 8,
            }}
          >
            <button
              onClick={onLogout}
              style={{
                width: "100%",
                padding: "12px 14px",
                minHeight: 44,
                background: "rgba(255,107,107,0.06)",
                border: "0.5px solid rgba(255,107,107,0.18)",
                borderRadius: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 12,
                color: "#ff6b6b",
                fontFamily: BRAND.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                textAlign: "left",
                touchAction: "manipulation",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
                logout
              </span>
              Sign out
            </button>
          </div>
        </nav>
      </div>
    </>
  );
};

export default HeaderMobilePanel;
